import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch, post, put } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { useAuth } from "@/lib/auth";
import { API_BASE } from "@/lib/utils";

export type SetupJourneyStatus = "in_progress" | "completed" | "skipped";

/** Step 1's product profile. Saved whole: the server merge is shallow. */
export interface SetupProduct {
  domain: string | null;
  name: string | null;
  description: string | null;
  entities: string[];
  apiDocsUrl: string | null;
  /** An OpenAPI file uploaded to the build agent's thread. */
  openApiFile: { attachmentId: string; filename: string } | null;
  noApi: boolean;
  webhooks: Array<{ name: string; description: string }>;
  direction: "in" | "out" | "both" | "access" | null;
  notes: Array<{ field: string; text: string }>;
  useCase: string | null;
  hopes: string | null;
}

export interface SetupJourney {
  /** The org the server resolved this journey for, or null when it could not.
   *  A null here is NOT an error response — see shouldSendToSetup. */
  orgId: string | null;
  /** in_progress while in the guided screens; skipped once they leave them for the platform. */
  status: SetupJourneyStatus;
  /** 1 product, 2 integrations, 3 embed, 4 customers. */
  step: number;
  connectorIds: string[];
  /** Display names of connectorIds, for the stalled-setup email. */
  connectorNames?: string[];
  useCase: string | null;
  widgetId: string | null;
  completedAt: string | null;
  screen: string | null;
  mode: "ai_tool" | "fastn" | null;
  aiTool: string | null;
  product: SetupProduct | null;
  productThreadId: string | null;
  /** The thread Home started from step 2's build prompt. */
  integrationThreadId: string | null;
  integrationChoice: "all" | "one" | "skip" | null;
  /** When, and on which host, the embedded widget first loaded on a page of the
   *  partner's own. Stamped by the api; a PUT cannot set it. */
  widgetLoadedAt: string | null;
  widgetLoadedOn: string | null;
  /** When, and for which customer, the api first saw a customer connection
   *  (step 4). Stamped by the api; a PUT cannot set it. */
  customerConnectedAt?: string | null;
  customerConnectedOrg?: string | null;
  /** When, and by whom, the journey was last saved. Stamped by the api's PUT;
   *  the stalled-setup nurture sweep reads them. */
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** The AI credits each finished step has granted. Answered by the server,
   *  never sent: a PUT and POST /onboarding/credits/claim grant them. */
  creditGrants?: OnboardingCreditGrant[];
}

export interface OnboardingCreditGrant {
  step: 1 | 2 | 3 | 4;
  credits: number;
  grantedAt: string;
}

/** The credits the journey's grants add up to. */
export function grantedCredits(journey: Pick<SetupJourney, "creditGrants"> | null | undefined): number {
  return (journey?.creditGrants ?? []).reduce((n, g) => n + g.credits, 0);
}

/**
 * Org-scoped state of the guided setup journey, stored on
 * org_configs.config.setupJourney. Drives both the wizard itself and the
 * first-login redirect in App.tsx.
 *
 * A failed read is an error, not an answer. It used to resolve to
 * "completed", which kept a blip from dropping an established account into
 * onboarding, but also handed anyone mid-onboarding a free pass into the
 * dashboard for the rest of the session. SetupGate already renders the
 * dashboard while there is no answer, so an outage still cannot lock builders
 * out; the difference is that the read keeps retrying, and the moment it
 * answers, an unfinished journey is sent back to /onboarding.
 */
/**
 * How the two onboarding gates' reads recover. Retried a few times up front,
 * then asked again every 30s for as long as they keep failing, so a transient
 * error can never stand in for "finished". Shared with useOnboardingStatus.
 */
export const GATE_READ_RETRY = {
  retry: 3,
  refetchInterval: (query: { state: { status: string } }) => (query.state.status === "error" ? 30_000 : false),
  refetchIntervalInBackground: true,
} as const;

export function useSetupJourney(opts?: { enabled?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery<SetupJourney>({
    queryKey: keys.onboarding.setup(orgId),
    queryFn: () => apiFetch<SetupJourney>(`${API_BASE}/v1/onboarding/setup`),
    enabled: (opts?.enabled ?? true) && !!orgId,
    staleTime: 60_000,
    ...GATE_READ_RETRY,
  });
}

export function useSaveSetupJourney() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    // A background bookmark of where the person is; a failure is recorded as
    // onboarding_step_failed (journey_save_failed), not put in front of them.
    meta: { silentError: true },
    mutationFn: (patch: Partial<SetupJourney>) =>
      apiFetch<SetupJourney>(`${API_BASE}/v1/onboarding/setup`, put(patch)),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.onboarding.setup(orgId), data);
      if ((data.creditGrants?.length ?? 0) > 0) void queryClient.invalidateQueries({ queryKey: keys.aiUsage.all(orgId) });
    },
  });
}

/**
 * Grant the credits of steps the server finds done. The server decides what is
 * done; this only says when to ask.
 */
export function useClaimOnboardingCredits() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: () =>
      apiFetch<{ creditGrants: OnboardingCreditGrant[] }>(`${API_BASE}/v1/onboarding/credits/claim`, post({}), { idempotent: true }),
    onSuccess: ({ creditGrants }) => {
      queryClient.setQueryData<SetupJourney>(keys.onboarding.setup(orgId), (j) => (j ? { ...j, creditGrants } : j));
      void queryClient.invalidateQueries({ queryKey: keys.aiUsage.all(orgId) });
    },
  });
}
